import { useLocation, Link } from "react-router-dom";
import { formatTime } from "../utility/formatDate";
import { getDoctorImageSrc } from "../utility/getDoctorImageSrc";

const ThankYou = () => {
  const location = useLocation();
  const { appointmentData, paymentResponse, formData, selectedDoctorImage } =
    location.state || {};

  const primaryColor = "text-teal-600";
  const primaryBg = "bg-teal-600";
  const primaryHover = "hover:bg-teal-700";

  const DetailRow = ({
    label,
    value,
    highlight = "font-medium text-gray-800",
  }) => (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={highlight}>{value}</span>
    </div>
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = date.toLocaleString("en-IN", { day: "2-digit" });
    const month = date.toLocaleString("en-IN", { month: "short" });
    // const year = date.getFullYear();

    // Build final format EXACTLY as required:
    // return `${weekday}, ${day} ${month} ${year}`;
    return ` ${day} ${month}`;
  }

  const formattedDate = formatDate(appointmentData.OTDate);
  const formattedTime = formatTime(appointmentData.FROMTIME);

  const appointmentDisplay = `${formattedDate} , ${formattedTime}`;

  return (
    <>
      <div className="min-h-screen bg-gray-50 mb-10">
        <section
          id="header-section"
          className="bg-white border-b border-gray-200 shadow-sm relative overflow-hidden flex items-center justify-center pt-0 pb-14 md:pt-22 md:pb-15"
          style={{
            backgroundImage: 'url("/asian-banner.jpg")',
            backgroundSize: "cover",
            backgroundPosition: "center bottom",
            backgroundColor: "#e0f2f7",
            minHeight: "310px",
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-800 tracking-tight">
              Thank You!
            </h1>
            <p className="mt-2 text-xl sm:text-xl text-gray-700 font-semibold">
              Your appointment is successfully booked.
            </p>
          </div>
        </section>

        {/* 2. MAIN CONTENT CARD */}
        <section className="max-w-5xl mx-auto -mt-16 sm:-mt-20 px-4 sm:px-6 lg:px-8 relative z-20">
          {" "}
          {/* Higher z-index to ensure it overlaps the banner */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden md:flex">
            {/* LEFT BOX — APPOINTMENT DETAILS */}
            <div className="p-6 sm:p-8 md:w-1/2 md:border-r border-gray-100 space-y-6">
              <h2 className="text-xl font-bold text-gray-800 border-b pb-3">
                Your Appointment Details
              </h2>

              {/* Doctor Profile */}
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center text-teal-600 border border-teal-200 flex-shrink-0">
                  {/* <img src="/doctor-dummy.png" alt="image" /> */}
                  <img
                    src={getDoctorImageSrc(selectedDoctorImage)}
                    onError={(e) => {
                      e.currentTarget.src = "/doctor-dummy.png";
                    }}
                    className="w-full h-full object-cover rounded-full"
                    alt={`${appointmentData?.DOCTORNM}-image`}
                  />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    Dr. {appointmentData?.DOCTORNM}
                  </p>
                  <p className={`text-sm font-medium ${primaryColor}`}>
                    {formData?.department}
                  </p>
                </div>
              </div>

              {/* Speciality & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 font-medium text-sm mb-1">
                    Speciality
                  </p>
                  <p className="text-base font-semibold text-gray-800">
                    {formData?.department}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium text-sm mb-1">
                    Consultation Type
                  </p>
                  <p className="text-base font-semibold text-gray-800">
                    {formData?.consultationType}
                  </p>
                </div>
              </div>

              {/* Patient Details and Time */}
              <div className="space-y-3 pt-2">
                <p className="text-base font-semibold text-gray-800 border-t pt-3">
                  Patient Details:
                </p>
                <DetailRow label="Name" value={appointmentData.NAME} />
                <DetailRow
                  label="Age & Gender"
                  value={`${appointmentData.AGE}, ${appointmentData.GENDERTYPE}`}
                />
                <DetailRow
                  label="Date & Time"
                  value={appointmentDisplay}
                  highlight="text-red-500 font-bold"
                />
              </div>

              {/* Total Fee & Payment ID */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between items-center bg-teal-50 p-3 rounded-lg border border-teal-100">
                  <span className="text-lg font-bold text-gray-800">
                    Total Fee
                  </span>
                  <span className="text-xl font-extrabold text-green-600">
                    ₹ {appointmentData.AMOUNT}
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT BOX — CONFIRMATION MESSAGE */}
            <div className="p-6 sm:p-8 md:w-1/2 flex flex-col justify-center items-center text-center bg-teal-50/50">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                {/* Green Checkmark Icon */}
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-800 mt-5 mb-1">
                Appointment Booked!
              </h3>

              <p className={`text-sm font-medium ${primaryColor} mb-8`}>
                Your details have been confirmed and saved.
              </p>

              <div className="bg-white p-4 rounded-lg w-full max-w-sm border border-gray-200 shadow-md">
                <p className="text-sm text-gray-500 mb-1">
                  Confirmation sent to:
                </p>
                <p className="text-lg font-bold text-gray-800 mb-2">
                  +91 {appointmentData.PHONE}
                </p>
                <p className="text-sm text-gray-800">{appointmentData.EMAIL}</p>
              </div>

              <div className="mt-4 w-full max-w-sm">
                <p className="text-xs text-gray-500 mb-1">Payment ID:</p>
                <p className="text-sm font-semibold text-gray-700 tracking-wide break-words">
                  {paymentResponse.razorpay_payment_id}
                </p>
              </div>

              <Link
                to="/"
                className={`mt-8 px-8 py-3 w-full max-w-sm ${primaryBg} text-white font-semibold rounded-lg shadow-lg transition duration-300 ${primaryHover} focus:outline-none focus:ring-4 focus:ring-teal-100`}
              >
                Go to Home Page
              </Link>

              <p className="text-xs text-gray-400 mt-4">
                Need help? Call us at +91 22 6698 6666
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ThankYou;
